import CsvParse from 'papaparse';
import { v4 as uuid } from 'uuid';
import { ImportActivityRow } from './activity-import-types';
import { ACTIVITY_COLUMNS, NAME_COLUMN, ONE_OFF_FIELDS, oneOffKey, SESSION_FIELDS } from './activity-import-columns';

const compact = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '');

const ACTIVITY_KEYS = new Map<string, string>([
  ...[NAME_COLUMN, ...ACTIVITY_COLUMNS].map((column) => [compact(column.key), column.key]),
  ['name', NAME_COLUMN.key],
  ['type', 'activityType'],
] as [string, string][]);

const SESSION_SUFFIXES = new Map<string, string>([
  ...SESSION_FIELDS.map((field) => [compact(field.suffix), field.suffix]),
  ['name', ''],
  ['description', '_desc'],
  ['starttime', '_start'],
  ['endtime', '_end'],
  ['minvolunteers', '_min'],
  ['maxvolunteers', '_max'],
] as [string, string][]);

const ONE_OFF_SUFFIXES = new Map<string, string>([
  ...Array.from(SESSION_SUFFIXES).filter(([, suffix]) => ONE_OFF_FIELDS.some((field) => field.suffix === suffix)),
  ['date', '_date'],
  ['dates', '_date'],
] as [string, string][]);

export const normaliseHeader = (header: string): string | null => {
  const key = compact(header);

  const activityKey = ACTIVITY_KEYS.get(key);
  if (activityKey) return activityKey;

  const oneOffMatch = key.match(/^sessiononeoff(\d+)(.*)$/);
  if (oneOffMatch) {
    const suffix = ONE_OFF_SUFFIXES.get(oneOffMatch[2]);
    return suffix === undefined ? null : oneOffKey(Number(oneOffMatch[1]), suffix);
  }

  const sessionMatch = key.match(/^session(\d+)(.*)$/);
  if (!sessionMatch) return null;

  const suffix = SESSION_SUFFIXES.get(sessionMatch[2]);
  return suffix === undefined ? null : `session_${Number(sessionMatch[1])}${suffix}`;
};

export const createEmptyRow = (values: Record<string, string> = {}): ImportActivityRow => ({
  id: uuid(),
  values,
  sessionSlots: 1,
  oneOffSlots: 0,
  resolvedAddress: null,
  geocodeStatus: values.address ? 'pending' : 'idle',
  createStatus: 'idle',
});

export const CSV_FORMAT_ERROR = 'CsvFormatError';

const csvFormatError = (message: string) => {
  const error = new Error(message);
  error.name = CSV_FORMAT_ERROR;
  return error;
};

export interface ParseResult {
  rows: ImportActivityRow[];
  ignoredHeaders: string[];
}

export const parseActivityCsv = (file: File): Promise<ParseResult> =>
  new Promise((resolve, reject) => {
    const ignoredHeaders: string[] = [];

    CsvParse.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: 'greedy',
      transformHeader: (header: string) => {
        const key = normaliseHeader(header);
        if (!key) {
          if (header.trim()) ignoredHeaders.push(header.trim());
          return `__ignored_${header}`;
        }
        return key;
      },
      complete: (result) => {
        const quoteErrors = result.errors.filter((error) => error.type === 'Quotes');
        if (quoteErrors.length > 0) {
          const rowNumbers = quoteErrors.map((error) => (error.row ?? 0) + 2).join(', ');
          reject(
            csvFormatError(`This file has mismatched quotes near row ${rowNumbers}. Fix the file and upload it again.`)
          );
          return;
        }

        const rows = result.data
          .map((raw) => {
            const values = Object.keys(raw).reduce((acc, key) => {
              if (key.startsWith('__ignored_')) return acc;
              const value = (raw[key] ?? '').trim();
              if (value) acc[key] = value;
              return acc;
            }, {} as Record<string, string>);
            return values;
          })
          .filter((values) => Object.keys(values).length > 0)
          .map((values) => createEmptyRow(values));

        resolve({ rows, ignoredHeaders });
      },
      error: (error) => reject(error),
    });
  });
